<template>
  <div id="risk-component-refactored" class="risk-calculator-container">
    <div class="info-panel">
      <h3 id="title-text" class="risk-title">Control de Riesgo</h3>
      <p id="text-tool" class="risk-text">
        El control de riesgo es una de las principales características cuando
        usamos la metodología Eurekers. Con esta calculadora financiera, usted
        controlará el riesgo en cada una de sus operaciones.
      </p>
    </div>

    <div class="form-panel">
      <div class="crypto-toggle">
        <label class="switch">
          <input type="checkbox" v-model="showDecimals" />
          <span class="slider"></span>
        </label>
        <span class="toggle-label">Crypto (afecta visualización decimal)</span>
      </div>

      <!-- First row: Riesgo máximo and Divisa riesgo -->
      <div class="form-row">
        <!-- Max Risk per Operation (User Currency) -->
        <div class="form-group">
          <label for="max_risk_per_operation_user_currency">
            Riesgo máximo por operación
          </label>
          <input
            type="text"
            id="max_risk_per_operation_user_currency"
            v-model="max_risk_per_operation_user_currency_str"
            @input="handleDecimalInput('max_risk_per_operation_user_currency')"
            placeholder="Riesgo máximo por operación"
            class="form-input"
            required
          />
          <small class="form-helper-text">
            Cantidad máxima que está dispuesto a arriesgar en esta operación
          </small>
        </div>

        <!-- User Currency Select with buttons -->
        <div class="form-group">
          <label>{{ userCurrencyConfig.label_name }}</label>
          <div class="currency-selector">
            <button 
              type="button" 
              class="currency-btn" 
              :class="{ 'currency-btn--active': user_currency === 'EUR' }" 
              @click="selectUserCurrency('EUR')"
            >
              EUR
            </button>
            <button 
              type="button" 
              class="currency-btn" 
              :class="{ 'currency-btn--active': user_currency === 'USD' }" 
              @click="selectUserCurrency('USD')"
            >
              USD
            </button>
            <select 
              v-model="userCurrencyDropdown" 
              class="form-select currency-select" 
              :class="{ 'currency-select--active': user_currency !== 'EUR' && user_currency !== 'USD' }"
              @change="handleUserCurrencyChange"
            >
              <option value="">Seleccionar otra divisa</option>
              <option v-for="opt in filteredUserCurrencyOptions" :key="opt.value" :value="opt.value">
                {{ opt.text }}
              </option>
            </select>
          </div>
          <small v-if="userCurrencyConfig.helper_select_permission && userCurrencyConfig.helper_text_select" class="form-helper-text">
            {{ userCurrencyConfig.helper_text_select }}
          </small>
        </div>
      </div>

      <!-- Second row: Riesgo máximo en divisa de operacion and Divisa operacion -->
      <div class="form-row">
        <!-- Max Risk in Operation Currency (Readonly) -->
        <div class="form-group">
          <label for="max_risk_op_currency">Riesgo máximo en divisa de operación</label>
          <input
            type="text"
            id="max_risk_op_currency"
            :value="formatNumber(max_risk_per_operation_op_currency_display)"
            readonly
            tabindex="-1"
            class="form-input form-input--readonly"
            disabled
          />
          <small class="form-helper-text">
            Riesgo máximo convertido a la divisa de operación
          </small>
        </div>

        <!-- Operation Currency Select with buttons -->
        <div class="form-group">
          <label>{{ operationCurrencyConfig.label_name }}</label>
          <div class="currency-selector">
            <button 
              type="button" 
              class="currency-btn" 
              :class="{ 'currency-btn--active': operation_currency === 'EUR' }" 
              @click="selectOperationCurrency('EUR')"
            >
              EUR
            </button>
            <button 
              type="button" 
              class="currency-btn" 
              :class="{ 'currency-btn--active': operation_currency === 'USD' }" 
              @click="selectOperationCurrency('USD')"
            >
              USD
            </button>
            <select 
              v-model="operationCurrencyDropdown" 
              class="form-select currency-select" 
              :class="{ 'currency-select--active': operation_currency !== 'EUR' && operation_currency !== 'USD' }"
              @change="handleOperationCurrencyChange"
            >
              <option value="">Seleccionar otra divisa</option>
              <option v-for="opt in filteredOperationCurrencyOptions" :key="opt.value" :value="opt.value">
                {{ opt.text }}
              </option>
            </select>
          </div>
          <small v-if="operationCurrencyConfig.helper_select_permission && operationCurrencyConfig.helper_text_select" class="form-helper-text">
            {{ operationCurrencyConfig.helper_text_select }}
          </small>
        </div>
      </div>

      <!-- Third row: Entry and Exit prices -->
      <div class="form-row">
        <!-- Entry Price -->
        <div class="form-group" v-if="input_risk_configs && input_risk_configs[1]">
          <label :for="input_risk_configs[1].input_key_to_save_user_values || 'entry_price'">
            {{ input_risk_configs[1].input_label }}
          </label>
          <input
            type="text"
            :id="input_risk_configs[1].input_key_to_save_user_values || 'entry_price'"
            v-model="entry_price_str"
            @input="handleDecimalInput('entry_price')"
            :placeholder="input_risk_configs[1].input_label"
            class="form-input"
            required
          />
          <small v-if="input_risk_configs[1].helper_permission && input_risk_configs[1].input_helper" class="form-helper-text">
            {{ input_risk_configs[1].input_helper }}
          </small>
        </div>
        <div class="form-group" v-else>
            <label for="entry_price">Precio de entrada</label>
            <input
              type="text"
              id="entry_price"
              v-model="entry_price_str"
              @input="handleDecimalInput('entry_price')"
              placeholder="Precio de entrada"
              class="form-input"
              required
            />
            <small class="form-helper-text">
              Ejemplo: 39.285
            </small>
        </div>

        <!-- Stop Loss Price -->
        <div class="form-group" v-if="input_risk_configs && input_risk_configs[2]">
          <label :for="input_risk_configs[2].input_key_to_save_user_values || 'stop_loss_price'">
            {{ input_risk_configs[2].input_label }}
          </label>
          <input
            type="text"
            :id="input_risk_configs[2].input_key_to_save_user_values || 'stop_loss_price'"
            v-model="stop_loss_price_str"
            @input="handleDecimalInput('stop_loss_price')"
            :placeholder="input_risk_configs[2].input_label"
            class="form-input"
            required
          />
          <small v-if="input_risk_configs[2].helper_permission && input_risk_configs[2].input_helper" class="form-helper-text">
            {{ input_risk_configs[2].input_helper }}
          </small>
        </div>
        <div class="form-group" v-else>
            <label for="stop_loss_price">Precio de salida</label>
            <input
              type="text"
              id="stop_loss_price"
              v-model="stop_loss_price_str"
              @input="handleDecimalInput('stop_loss_price')"
              placeholder="Precio de salida"
              class="form-input"
              required
            />
            <small class="form-helper-text">
              Ejemplo: 35.613
            </small>
        </div>
      </div>
    </div>

    <hr class="separator" />

    <div class="results-panel">
      <div class="result-box">
        <p class="result-label">NÚMERO DE ACCIONES A COMPRAR</p>
        <p class="result-value">{{ formatNumber(shares_to_buy) }}</p>
      </div>
      <div class="result-box">
        <p class="result-label">COTIZACIÓN (1 EUR EN DIVISA OP.)</p>
        <p class="result-value">{{ formatNumber(eur_to_op_currency_rate, true) }}</p>
      </div>
      <div class="result-box">
        <p class="result-label">MONTANTE (EN DIVISA OPERACIÓN)</p>
        <p class="result-value">{{ formatNumber(total_amount_operation_currency) }}</p>
      </div>
      <div class="result-box">
        <p class="result-label">MONTANTE EN EUROS</p>
        <p class="result-value">{{ formatNumber(total_amount_euros) }}</p>
      </div>
    </div>
     <div v-if="isLoading" class="loading-indicator">Calculando...</div>
  </div>
</template>

<script>
import inputRiskFirstOriginal from './configs/risk/input_risk_first.json';
import inputRiskOriginal from './configs/risk/input_risk.json';
import selectRiskOriginal from './configs/risk/select_risk.json';
import APICaller from "@/components/codeComponents/APICaller.js"; // Assuming path

const DEFAULT_SELECT_CONFIG = { 
    options: [], 
    label_name: 'Error de Configuración', 
    helper_select_permission: false, 
    helper_text_select: '' 
};

export default {
  name: 'RiskRefactored',
  mixins: [APICaller],
  metaInfo() {
    return {
      title: 'Control de riesgo Eurekers',
      meta: [
        { name: 'description', content: "Calculadora financiera para controlar el riesgo en operaciones, refactorizada para mayor claridad y eficiencia."}
      ]
    }
  },
  created() {
    // Initialize component with data from JSON configs
    this.initializeFromConfigs();
  },
  mounted() {
    // Trigger initial calculation since EUR is set as default
    this._triggerRecalculation();
  },
  data() {
    // Find the user currency config (was using incorrect ref name)
    const userCurrencyConfig = (selectRiskOriginal || []).find(s => s.ref === 'select_currency') || 
                               { ...DEFAULT_SELECT_CONFIG, label_name: 'Divisa del Usuario (Conf Err)' };
    
    // Find the operation currency config (was using incorrect ref name)
    const operationCurrencyConfig = (selectRiskOriginal || []).find(s => s.ref === 'select_currency_operation') ||
                                    { ...DEFAULT_SELECT_CONFIG, label_name: 'Divisa de Operación (Conf Err)' };
    
    // Fix the options format for select dropdowns
    const formatSelectOptions = (options) => {
      if (!Array.isArray(options)) return [];
      return options.map(opt => ({
        value: opt.name, // Use name as value (e.g., "USD")
        text: `${opt.name} - ${opt.value}` // Display name and description (e.g., "USD - American Dollar")
      }));
    };

    return {
      // Input values as strings for display
      max_risk_per_operation_user_currency_str: '',
      entry_price_str: '',
      stop_loss_price_str: '',
      
      // User selections - set EUR as default
      user_currency: 'EUR',
      operation_currency: 'EUR',
      
      // Dropdown models (separate from actual currency values)
      userCurrencyDropdown: '',
      operationCurrencyDropdown: '',

      // Configs from JSON - ensuring they are arrays
      input_risk_first_configs: inputRiskFirstOriginal || [],
      input_risk_configs: inputRiskOriginal || [],
      
      // Specific configs for selects for easier and safer access in template
      userCurrencyConfig,
      operationCurrencyConfig,
      user_currency_options: formatSelectOptions(userCurrencyConfig.options || []),
      operation_currency_options: formatSelectOptions(operationCurrencyConfig.options || []),

      // API-fetched and intermediate values
      user_currency_to_op_currency_rate: 0,
      eur_to_op_currency_rate: 0,
      max_risk_per_operation_op_currency_display: 0,

      // Result values
      shares_to_buy: 0,
      total_amount_operation_currency: 0,
      total_amount_euros: 0,

      showDecimals: false,
      isLoading: false,
    };
  },
  computed: {
    // Convert string inputs to numbers for calculations
    max_risk_per_operation_user_currency() {
      return this.parseDecimalInput(this.max_risk_per_operation_user_currency_str);
    },
    entry_price() {
      return this.parseDecimalInput(this.entry_price_str);
    },
    stop_loss_price() {
      return this.parseDecimalInput(this.stop_loss_price_str);
    },
    // Filter out EUR and USD from dropdown options
    filteredUserCurrencyOptions() {
      return this.user_currency_options.filter(opt => opt.value !== 'EUR' && opt.value !== 'USD');
    },
    filteredOperationCurrencyOptions() {
      return this.operation_currency_options.filter(opt => opt.value !== 'EUR' && opt.value !== 'USD');
    }
  },
  watch: {
    max_risk_per_operation_user_currency_str: '_triggerRecalculation',
    entry_price_str: '_triggerRecalculation',
    stop_loss_price_str: '_triggerRecalculation',
    user_currency: '_triggerRecalculation',
    operation_currency: '_triggerRecalculation',
    showDecimals: '_triggerRecalculation',
  },
  methods: {
    initializeFromConfigs() {
      // Set default values from configs if available
      try {
        // Log the configs for debugging
        console.log('Input Risk First Configs:', this.input_risk_first_configs);
        console.log('Input Risk Configs:', this.input_risk_configs);
        console.log('Select Risk Configs - User Currency:', this.userCurrencyConfig);
        console.log('Select Risk Configs - Operation Currency:', this.operationCurrencyConfig);
      } catch (error) {
        console.error('Error initializing from configs:', error);
      }
    },
    
    // Currency selection methods
    selectUserCurrency(currency) {
      this.user_currency = currency;
      this.userCurrencyDropdown = ''; // Reset dropdown to show placeholder
      this._triggerRecalculation();
    },
    
    selectOperationCurrency(currency) {
      this.operation_currency = currency;
      this.operationCurrencyDropdown = ''; // Reset dropdown to show placeholder
      this._triggerRecalculation();
    },
    
    handleUserCurrencyChange() {
      if (this.userCurrencyDropdown) {
        this.user_currency = this.userCurrencyDropdown;
        this._triggerRecalculation();
      }
    },
    
    handleOperationCurrencyChange() {
      if (this.operationCurrencyDropdown) {
        this.operation_currency = this.operationCurrencyDropdown;
        this._triggerRecalculation();
      }
    },
    
    getInputStep() {
      return this.showDecimals ? '0.01' : '1';
    },

    // Parse decimal input string to number
    parseDecimalInput(value) {
      if (!value) return 0;
      // Replace commas with dots for decimal
      const normalizedValue = value.toString().replace(',', '.');
      const parsed = parseFloat(normalizedValue);
      return isNaN(parsed) ? 0 : parsed;
    },

    // Handle decimal input with comma to dot conversion
    handleDecimalInput(fieldId) {
      // Map field IDs to their corresponding string properties
      const fieldToProperty = {
        'max_risk_per_operation_user_currency': 'max_risk_per_operation_user_currency_str',
        'entry_price': 'entry_price_str',
        'stop_loss_price': 'stop_loss_price_str'
      };
      
      const propName = fieldToProperty[fieldId];
      
      if (this[propName] !== undefined) {
        // Replace commas with dots
        let value = this[propName].toString().replace(',', '.');
        
        // Remove all non-numeric characters except the decimal point
        value = value.replace(/[^\d.]/g, '');
        
        // Ensure only one decimal point
        const parts = value.split('.');
        if (parts.length > 2) {
          value = parts[0] + '.' + parts.slice(1).join('');
        }
        
        // Add a "0" before the dot if it starts with a dot
        if (value.startsWith('.')) {
          value = '0' + value;
        }
        
        this[propName] = value;
      }
    },

    _resetResultsAndRates() {
        this.shares_to_buy = 0;
        this.total_amount_operation_currency = 0;
        this.total_amount_euros = 0;
        this.user_currency_to_op_currency_rate = 0;
        this.eur_to_op_currency_rate = 0;
        this.max_risk_per_operation_op_currency_display = 0;
    },
    
    _arePrimaryInputsSet() {
      return (
        this.max_risk_per_operation_user_currency > 0 &&
        this.entry_price > 0 &&
        this.stop_loss_price > 0 &&
        this.user_currency &&
        this.operation_currency
      );
    },

    async _triggerRecalculation() {
      if (!this._arePrimaryInputsSet()) {
        this._resetResultsAndRates();
        // Only clear results related to calculations, not necessarily the displayed risk in op currency if rates were fetched
        this.shares_to_buy = 0;
        this.total_amount_operation_currency = 0;
        this.total_amount_euros = 0;
        // max_risk_per_operation_op_currency_display will be updated by _calculateMaxRiskInOpCurrencyDisplay
        // which might be called if currencies are set even if other inputs are not.
        // For safety, if primary inputs for calculation are not set, risk in op currency should also be 0 if it depends on user risk amount.
        if (!(this.max_risk_per_operation_user_currency > 0)) {
             this.max_risk_per_operation_op_currency_display = 0;
        }
        return;
      }
      
      this.isLoading = true;
      try {
        await this._fetchCurrencyData(); // This also calls _calculateMaxRiskInOpCurrencyDisplay
        this._performRiskCalculations();
      } catch (error) {
        console.error('Error during calculation:', error);
        this._resetResultsAndRates();
      } finally {
        this.isLoading = false;
      }
    },

    async _fetchCurrencyData() {
      if (!this.user_currency || !this.operation_currency) {
        if (this.$vueOnToast) this.$vueOnToast.pop('warning', 'Faltan divisas', 'Por favor, seleccione ambas divisas.');
        this.user_currency_to_op_currency_rate = 0;
        this.eur_to_op_currency_rate = 0;
        this._calculateMaxRiskInOpCurrencyDisplay(); // Update dependent display
        return Promise.reject("Missing currencies");
      }

      try {
        // Fetch rates sequentially to ensure dependent calculations have fresh data
        await this._updateUserCurrencyToOpRate();
        this._calculateMaxRiskInOpCurrencyDisplay(); // Update display after user_currency_to_op_currency_rate is fetched
        await this._updateEurToOpRate();
      } catch (error) {
        throw error; // Re-throw to be caught by _triggerRecalculation
      }
    },

    _updateUserCurrencyToOpRate() {
      return new Promise((resolve, reject) => {
        if (this.user_currency === this.operation_currency) {
          this.user_currency_to_op_currency_rate = 1;
          resolve();
          return;
        }
        const url = `/api/v1/calculator-exchanges/${this.user_currency}/${this.operation_currency}`;
        
        const successCallback = response => {
          const rate = response?.data?.chart?.result?.[0]?.meta?.chartPreviousClose;
          if (typeof rate === 'number') {
            this.user_currency_to_op_currency_rate = parseFloat(rate.toFixed(5));
            resolve();
          } else {
            if (this.$vueOnToast) this.$vueOnToast.pop('error', 'Error de datos', `Respuesta inesperada para ${this.user_currency}/${this.operation_currency}.`);
            this.user_currency_to_op_currency_rate = 0;
            reject(new Error('Invalid rate data for user currency to op'));
          }
        };
        const failureCallback = (error) => {
          if (this.$vueOnToast) this.$vueOnToast.pop('error', 'Error API', `No se pudo obtener tasa ${this.user_currency}/${this.operation_currency}.`);
          this.user_currency_to_op_currency_rate = 0;
          reject(error);
        };

        const successHandler = new APICaller.SuccessHandler(successCallback);
        const failureHandler = new APICaller.FailureHandler('API_ERR_USER_OP', `Error API ${this.user_currency}/${this.operation_currency}`, failureCallback);
        this._getAPICall(url, successHandler, failureHandler);
      });
    },

    _updateEurToOpRate() {
      return new Promise((resolve, reject) => {
        if (this.operation_currency === 'EUR') {
          this.eur_to_op_currency_rate = 1;
          resolve();
          return;
        }
        const url = `/api/v1/calculator-exchanges/EUR/${this.operation_currency}`;

        const successCallback = response => {
          const rate = response?.data?.chart?.result?.[0]?.meta?.chartPreviousClose;
           if (typeof rate === 'number') {
            this.eur_to_op_currency_rate = parseFloat(rate.toFixed(5));
            resolve();
          } else {
            if (this.$vueOnToast) this.$vueOnToast.pop('error', 'Error de datos', `Respuesta inesperada para EUR/${this.operation_currency}.`);
            this.eur_to_op_currency_rate = 0;
            reject(new Error('Invalid rate data for EUR to op'));
          }
        };
        const failureCallback = (error) => {
          if (this.$vueOnToast) this.$vueOnToast.pop('error', 'Error API', `No se pudo obtener tasa EUR/${this.operation_currency}.`);
          this.eur_to_op_currency_rate = 0;
          reject(error);
        };
        
        const successHandler = new APICaller.SuccessHandler(successCallback);
        const failureHandler = new APICaller.FailureHandler('API_ERR_EUR_OP', `Error API EUR/${this.operation_currency}`, failureCallback);
        this._getAPICall(url, successHandler, failureHandler);
      });
    },
    
    _calculateMaxRiskInOpCurrencyDisplay() {
      const risk = parseFloat(this.max_risk_per_operation_user_currency);
      if (!isNaN(risk) && risk > 0 && typeof this.user_currency_to_op_currency_rate === 'number' && this.user_currency_to_op_currency_rate !== 0) {
        this.max_risk_per_operation_op_currency_display = risk * this.user_currency_to_op_currency_rate;
      } else if (!isNaN(risk) && risk > 0 && this.user_currency === this.operation_currency) { // Rate is 1
        this.max_risk_per_operation_op_currency_display = risk;
      } else {
        this.max_risk_per_operation_op_currency_display = 0;
      }
    },

    _performRiskCalculations() {
      const entry = parseFloat(this.entry_price);
      const stop = parseFloat(this.stop_loss_price);

      if (entry <= stop) {
        if (this.$vueOnToast) this.$vueOnToast.pop('warning', 'Entrada inválida', 'El precio de entrada debe ser mayor que el precio de stop loss.');
        this.shares_to_buy = 0;
        this.total_amount_operation_currency = 0;
        this.total_amount_euros = 0;
        return;
      }

      const riskInOperationCurrency = this.max_risk_per_operation_op_currency_display;

      if (riskInOperationCurrency <= 0 || (entry - stop) <= 0) {
         this.shares_to_buy = 0;
      } else {
        this.shares_to_buy = riskInOperationCurrency / (entry - stop);
      }
      
      this.total_amount_operation_currency = this.shares_to_buy * entry;

      if (this.operation_currency === 'EUR') {
        this.total_amount_euros = this.total_amount_operation_currency;
      } else if (this.eur_to_op_currency_rate && this.eur_to_op_currency_rate !== 0) {
        this.total_amount_euros = this.total_amount_operation_currency / this.eur_to_op_currency_rate;
      } else {
        this.total_amount_euros = 0;
      }
    },

    formatNumber(value, isExchangeRate = false) {
      const num = Number(value);
      if (value == null || isNaN(num) || !isFinite(num)) {
        return this.showDecimals ? '0.00' : '0';
      }
      
      if (isExchangeRate) {
        return num.toFixed(3); 
      }
      
      return this.showDecimals ? num.toFixed(2) : num.toFixed(0);
    },
  },
};
</script>

<style lang="scss" scoped>
// Color variables (assumed to be globally accessible or defined elsewhere)
// --color-primary, --color-secondary, etc.

.risk-calculator-container {
  padding: 20px;
  background-color: var(--color-background);
  color: var(--color-text-primary);
  max-width: 900px;
  margin: 30px auto; // Centered with some margin
  border: 1px solid var(--color-outline);
  border-radius: 8px;
}

.info-panel {
  margin-bottom: 25px;
  .risk-title {
    font-size: 22px;
    font-weight: 600;
    color: var(--color-text-primary);
    margin-bottom: 8px;
  }
  .risk-text {
    font-size: 14px;
    line-height: 1.6;
    color: var(--color-text-secondary);
  }
}

.form-panel {
  margin-bottom: 25px;
}

.form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 20px; // Creates space between items
  margin-bottom: 20px;
}

.form-group {
  flex: 1 1 calc(50% - 10px); // Each group takes up to 50% width, minus half the gap
  min-width: 250px; // Minimum width before wrapping fully
  display: flex;
  flex-direction: column;

  label {
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 500;
    color: var(--color-text-secondary);
  }
   p { // For error messages when config is missing
    color: var(--color-error);
    font-size: 13px;
  }
}

.form-input,
.form-select {
  padding: 10px 12px;
  border: 1px solid var(--color-outline);
  border-radius: 4px;
  background-color: var(--color-surface);
  color: var(--color-text-primary);
  font-size: 16px;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px var(--color-primary-muted, rgba(var(--color-primary-rgb), 0.2)); // Assuming a muted version or define one
  }
}

/* Remove arrows/spinners from number inputs */
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}

.form-input--readonly {
  background-color: var(--color-muted);
  cursor: not-allowed;
  user-select: none;
  
  &:focus {
    outline: none;
    border-color: var(--color-outline);
    box-shadow: none;
  }
}

.form-helper-text {
  margin-top: 6px;
  font-size: 12px;
  color: var(--color-text-inactive);
}

.separator {
  border: none;
  border-top: 1px solid var(--color-outline);
  margin: 30px 0;
}

.results-panel {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.result-box {
  background-color: var(--color-surface);
  padding: 15px;
  border-radius: 4px;
  border: 1px solid var(--color-outline);
  display: flex;
  flex-direction: column;

  .result-label {
    font-size: 13px;
    font-weight: 600;
    color: var(--color-text-secondary);
    margin-bottom: 8px;
    text-transform: uppercase;
    white-space: normal;
    overflow: visible;
    height: 2.6em; /* Allow for two lines of text */
    line-height: 1.3;
  }
  .result-value {
    font-size: 20px;
    font-weight: 700;
    color: var(--color-highlight);
    margin: auto 0 0 0;
  }
}

.crypto-toggle {
  display: flex;
  align-items: center;
  margin-bottom: 25px;
  .toggle-label {
    margin-left: 10px;
    color: var(--color-text-primary);
    font-size: 14px;
  }
  .switch {
    position: relative;
    display: inline-block;
    width: 50px; // Smaller toggle
    height: 28px;
    input {
      opacity: 0;
      width: 0;
      height: 0;
    }
  }
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--color-muted);
    transition: .4s;
    border-radius: 28px;
    &:before {
      position: absolute;
      content: "";
      height: 20px;
      width: 20px;
      left: 4px;
      bottom: 4px;
      background-color: var(--color-text-on-highlight);
      transition: .4s;
      border-radius: 50%;
    }
  }
  input:checked + .slider {
    background-color: var(--color-highlight);
  }
  input:checked + .slider:before {
    transform: translateX(22px);
  }
}

.loading-indicator {
    margin-top: 15px;
    padding: 10px;
    text-align: center;
    color: var(--color-text-secondary);
    background-color: var(--color-active-surface);
    border-radius: 4px;
}

// Currency selector styles
.currency-selector {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 5px;
}

.currency-btn {
  padding: 8px 16px;
  background-color: var(--color-surface);
  border: 1px solid var(--color-outline);
  border-radius: 4px;
  color: var(--color-text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 100%;
  
  &:hover {
    background-color: var(--color-active-surface);
  }
  
  &--active {
    background-color: var(--color-active-surface);
    color: var(--color-highlight);
    border-color: var(--color-highlight);
    
    &:hover {
      background-color: var(--color-active-surface);
    }
  }
}

.currency-select {
  flex: 1;
  min-width: 0;
  cursor: pointer;
  color: var(--color-text-secondary);
  
  &--active {
    background-color: var(--color-active-surface);
    color: var(--color-highlight);
    border-color: var(--color-highlight);
    
    &:hover {
      background-color: var(--color-active-surface);
    }
  }

  &:focus {
      border-color: var(--color-highlight);
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .form-row {
    flex-direction: column; // Stack form groups vertically
    gap: 0; // Remove gap if stacking, rely on form-group margin
  }
  .form-group {
    flex-basis: 100%; // Full width for form groups
    margin-bottom: 20px; // Add margin back for stacked items
    &:last-child {
        margin-bottom: 0;
    }
  }
  .results-panel {
    grid-template-columns: 1fr; // Stack result boxes
  }
  
  .currency-selector {
    flex-wrap: wrap;
    
    .currency-btn {
      flex: 1;
      min-width: 80px;
    }
    
    .currency-select {
      flex: 100%;
      margin-top: 8px;
    }
  }
}

@media (max-width: 480px) {
  .risk-calculator-container {
    padding: 15px;
    margin: 15px;
  }
  .info-panel .risk-title {
    font-size: 20px;
  }
  .info-panel .risk-text,
  .form-group label,
  .crypto-toggle .toggle-label {
    font-size: 13px;
  }
  .result-box .result-value {
    font-size: 18px;
  }
}
</style>